import { useEffect, useState, type ReactNode, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { ActiveProduct, SubmitPublicLeadRequest } from "@greatway/api-client";
import { submitPublicLead, getActiveProducts, ApiError } from "../../lib/api";
import TurnstileWidget from "../../components/TurnstileWidget";

type Errors = Record<string, string>;

interface FormState {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  address1: string;
  town: string;
  postcode: string;
  productInterest: string;
  comments: string;
  widthM: string;
  lengthM: string;
  heightM: string;
  websiteUrl: string; // honeypot — must stay empty
}

const EMPTY: FormState = {
  firstName: "", lastName: "", phone: "", email: "", address1: "", town: "",
  postcode: "", productInterest: "", comments: "", widthM: "", lengthM: "", heightM: "", websiteUrl: "",
};

const inputCls = (hasError: boolean) =>
  `w-full h-12 px-3 border rounded-md text-sm transition-colors ${
    hasError ? "border-red-500 ring-2 ring-red-500/50" : "border-gray-300 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500"
  }`;

function Field({ label, htmlFor, error, children }: { label: string; htmlFor: string; error?: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function validate(f: FormState): Errors {
  const e: Errors = {};
  if (!f.firstName.trim()) e.firstName = "First name is required.";
  if (!f.lastName.trim()) e.lastName = "Last name is required.";
  if (f.phone.replace(/\D/g, "").length < 8) e.phone = "Enter a valid phone number.";
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) e.email = "Enter a valid email address.";
  if (!f.address1.trim()) e.address1 = "Street address is required.";
  if (!f.town.trim()) e.town = "Suburb / town is required.";
  if (!/^\d{4}$/.test(f.postcode)) e.postcode = "Enter a 4-digit postcode.";
  return e;
}

const toDecimal = (s: string): number | undefined => {
  const t = s.trim();
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
};

export default function EnquireFormPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [turnstileToken, setTurnstileToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [products, setProducts] = useState<ActiveProduct[] | null>(null);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    getActiveProducts()
      .then((p) => setProducts(p))
      .catch(() => setProducts(null)) // not anonymously reachable → fall back to free text
      .finally(() => setProductsLoaded(true));
  }, []);

  const update = <K extends keyof FormState>(key: K, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setServerError(null);
    const e = validate(form);
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    const body: SubmitPublicLeadRequest = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      address1: form.address1.trim(),
      town: form.town.trim(),
      postcode: form.postcode.trim(),
      productInterest: form.productInterest.trim() || null,
      comments: form.comments.trim() || null,
      widthM: toDecimal(form.widthM) ?? null,
      lengthM: toDecimal(form.lengthM) ?? null,
      heightM: toDecimal(form.heightM) ?? null,
      cfTurnstileResponse: turnstileToken || null,
      websiteUrl: form.websiteUrl || null, // honeypot
    };

    setSubmitting(true);
    try {
      const result = await submitPublicLead(body);
      // Blind: token is populated only on a genuine create. Either way we go to the confirmation page.
      const token = result.confirmationToken;
      navigate(token ? `/enquire/confirmation#token=${encodeURIComponent(token)}` : "/enquire/confirmation");
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setUnavailable(true);
      } else if (err instanceof ApiError && err.status === 400 && err.problem?.errors) {
        const fieldErrors: Errors = {};
        for (const [k, msgs] of Object.entries(err.problem.errors)) {
          const key = k.charAt(0).toLowerCase() + k.slice(1);
          fieldErrors[key] = msgs[0] ?? "Invalid value.";
        }
        setErrors(fieldErrors);
      } else {
        setServerError("Something went wrong sending your enquiry. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (unavailable) {
    return (
      <div className="mx-auto max-w-lg rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm">
        <h1 className="text-xl font-semibold text-gray-900">Enquiries are temporarily unavailable</h1>
        <p className="mt-2 text-sm text-gray-600">
          Our enquiry form is currently offline. Please try again shortly, or call us directly.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Get a free quote</h1>
      <p className="mt-1 text-sm text-gray-600">
        Tell us about your project. Fields marked with * are required.
      </p>

      <form onSubmit={onSubmit} noValidate className="mt-6 space-y-5 rounded-lg border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="First name *" htmlFor="firstName" error={errors.firstName}>
            <input id="firstName" type="text" autoComplete="given-name" className={inputCls(!!errors.firstName)}
              value={form.firstName} onChange={(e) => update("firstName", e.target.value)} />
          </Field>
          <Field label="Last name *" htmlFor="lastName" error={errors.lastName}>
            <input id="lastName" type="text" autoComplete="family-name" className={inputCls(!!errors.lastName)}
              value={form.lastName} onChange={(e) => update("lastName", e.target.value)} />
          </Field>
          <Field label="Phone *" htmlFor="phone" error={errors.phone}>
            <input id="phone" type="tel" inputMode="tel" autoComplete="tel" className={inputCls(!!errors.phone)}
              value={form.phone} onChange={(e) => update("phone", e.target.value)} />
          </Field>
          <Field label="Email *" htmlFor="email" error={errors.email}>
            <input id="email" type="email" inputMode="email" autoComplete="email" className={inputCls(!!errors.email)}
              value={form.email} onChange={(e) => update("email", e.target.value)} />
          </Field>
        </div>

        <Field label="Street address *" htmlFor="address1" error={errors.address1}>
          <input id="address1" type="text" autoComplete="address-line1" className={inputCls(!!errors.address1)}
            value={form.address1} onChange={(e) => update("address1", e.target.value)} />
        </Field>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Suburb / town *" htmlFor="town" error={errors.town}>
            <input id="town" type="text" autoComplete="address-level2" className={inputCls(!!errors.town)}
              value={form.town} onChange={(e) => update("town", e.target.value)} />
          </Field>
          <Field label="Postcode *" htmlFor="postcode" error={errors.postcode}>
            <input id="postcode" type="text" inputMode="numeric" autoComplete="postal-code" maxLength={4}
              className={inputCls(!!errors.postcode)} value={form.postcode} onChange={(e) => update("postcode", e.target.value)} />
          </Field>
        </div>

        <Field label="What are you interested in?" htmlFor="productInterest" error={errors.productInterest}>
          {productsLoaded && products && products.length > 0 ? (
            <select id="productInterest" className={inputCls(!!errors.productInterest)}
              value={form.productInterest} onChange={(e) => update("productInterest", e.target.value)}>
              <option value="">Select a product…</option>
              {products.map((p) => (
                <option key={p.id} value={p.productCode || p.productName}>{p.productName}</option>
              ))}
            </select>
          ) : (
            <input id="productInterest" type="text" className={inputCls(!!errors.productInterest)}
              placeholder="e.g. Outback patio, carport, pergola" value={form.productInterest}
              onChange={(e) => update("productInterest", e.target.value)} />
          )}
        </Field>

        <fieldset className="grid grid-cols-3 gap-3">
          <legend className="mb-1 text-sm font-medium text-gray-700">Approx. dimensions (m) — optional</legend>
          <input aria-label="Width (m)" type="text" inputMode="decimal" placeholder="Width" className={inputCls(false)}
            value={form.widthM} onChange={(e) => update("widthM", e.target.value)} />
          <input aria-label="Length (m)" type="text" inputMode="decimal" placeholder="Length" className={inputCls(false)}
            value={form.lengthM} onChange={(e) => update("lengthM", e.target.value)} />
          <input aria-label="Height (m)" type="text" inputMode="decimal" placeholder="Height" className={inputCls(false)}
            value={form.heightM} onChange={(e) => update("heightM", e.target.value)} />
        </fieldset>

        <Field label="Anything else we should know?" htmlFor="comments">
          <textarea id="comments" rows={4} maxLength={2000}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50"
            value={form.comments} onChange={(e) => update("comments", e.target.value)} />
        </Field>

        {/* Honeypot — hidden from real users; bots that fill it are silently rejected server-side. */}
        <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden" tabIndex={-1}>
          <label htmlFor="websiteUrl">Leave this field empty</label>
          <input id="websiteUrl" type="text" tabIndex={-1} autoComplete="off"
            value={form.websiteUrl} onChange={(e) => update("websiteUrl", e.target.value)} />
        </div>

        <TurnstileWidget onToken={setTurnstileToken} />

        {serverError && <p className="text-sm text-red-600">{serverError}</p>}

        <button type="submit" disabled={submitting}
          className="inline-flex h-12 w-full items-center justify-center rounded-md bg-indigo-600 px-6 text-base font-medium text-white shadow-sm transition-colors hover:bg-indigo-700 focus-visible:ring-2 focus-visible:ring-indigo-500/50 disabled:opacity-50 sm:w-auto">
          {submitting ? "Sending…" : "Send enquiry"}
        </button>
      </form>
    </div>
  );
}
